/** Open auto popovers (menus, selects) — light-dismiss on Escape / outside click. */
export const OPEN_AUTO_POPOVER_SELECTOR = '[popover]:not([popover="manual" i]):popover-open';

/**
 * Non-modal stacked dialogs (standard + floating). Native showModal() is excluded.
 * Floating panels are shown as manual popovers (see showFloatingPanel), which never
 * set the `open` attribute — they carry `data-open` instead.
 */
export const STACKED_DIALOG_SELECTOR = 'dialog[open]:not(:modal), dialog[data-open]';

/**
 * Standard modals only — excludes floating panels, which sit above the band on
 * their own z-index and don't participate in the standard modal stack.
 */
export const STANDARD_MODAL_SELECTOR = 'dialog[open]:not(:modal):not([data-floating])';

/**
 * Dialogs portal to <body> in open order, so the last dialog matching
 * `selector` is the most recently opened / topmost.
 */
export function isLastOpenDialog(dialog: HTMLDialogElement, selector: string): boolean {
  const open = safeQuerySelectorAll<HTMLDialogElement>(selector);
  return open.length === 0 || open[open.length - 1] === dialog;
}

/**
 * True when `element` is inside the topmost open stacked dialog — a standard
 * modal or a floating panel (e.g. Ask), whichever was opened most recently.
 * Same stack rule Escape uses. Native `showModal()` dialogs sit in the top
 * layer and are excluded.
 */
export function isInTopmostStackedDialog(element: Element | null): boolean {
  const dialog = element?.closest('dialog');
  if (!(dialog instanceof HTMLDialogElement) || !isDialogShown(dialog)) {
    return false;
  }
  return isLastOpenDialog(dialog, STACKED_DIALOG_SELECTOR);
}

/** Open via show()/showModal() (`open`), or shown as a floating popover (`data-open`). */
function isDialogShown(dialog: HTMLDialogElement): boolean {
  return dialog.open || dialog.hasAttribute('data-open');
}

// The topmost standard modal (they share --modal-zindex and stack by DOM
// order). Excludes native showModal dialogs (:modal, in the top layer) and
// floating panels ([data-floating], which sit above the band on their own).
export function isTopmostModal(dialog: HTMLDialogElement): boolean {
  return isLastOpenDialog(dialog, STANDARD_MODAL_SELECTOR);
}

export function shouldCloseOnEscape(dialog: HTMLDialogElement, event: KeyboardEvent): boolean {
  if (event.key !== 'Escape' || event.defaultPrevented || !isDialogShown(dialog)) {
    return false;
  }
  // A native modal dialog (showModal alert/confirm) above closes via its own
  // cancel event.
  if (safeQuerySelector('dialog:modal')) {
    return false;
  }
  // An open auto popover (menu, select) light-dismisses on this Escape
  if (safeQuerySelector(OPEN_AUTO_POPOVER_SELECTOR)) {
    return false;
  }
  // Escape closes the last-opened non-modal dialog in DOM order — a standard modal
  // or the floating Ask panel, whichever was opened most recently.
  return isLastOpenDialog(dialog, STACKED_DIALOG_SELECTOR);
}

/**
 * The browser light-dismisses an open auto popover (anchored sheet, menu)
 * during pointerdown — before the resulting click reaches our listeners — so
 * by click time the popover is gone from both the DOM and the event's
 * composedPath, and its dismissing click would read as a click outside the
 * dialog. Records whether an auto popover was open at pointerdown; the
 * returned function reports (and resets) that flag so such clicks can be
 * ignored. The click itself still propagates normally, keeping the rest of
 * the page live (e.g. switching between toolbar menus with one click).
 */
export function trackAutoPopoverDismiss(signal: AbortSignal): () => boolean {
  let isDismissingAutoPopover = false;
  document.addEventListener(
    'pointerdown',
    () => {
      isDismissingAutoPopover = !!safeQuerySelector(OPEN_AUTO_POPOVER_SELECTOR);
    },
    { signal, capture: true },
  );
  return () => {
    const wasDismissing = isDismissingAutoPopover;
    isDismissingAutoPopover = false;
    return wasDismissing;
  };
}

export function isOutsideModalClick(dialog: HTMLDialogElement, event: MouseEvent): boolean {
  // Synthetic clicks (element.click(), e.g. the hidden file-picker input
  // appended to <body>) are not user interactions and must not light-dismiss
  if (!event.isTrusted) {
    return false;
  }
  if (!dialog.open || !isTopmostModal(dialog)) {
    return false;
  }
  // A click can re-render and detach the clicked element (e.g. the modal resize
  // button whose icon swaps on click) before the event finishes bubbling to the
  // document, making contains()/closest() checks read as outside the dialog. So
  // Use the event's dispatch-time path, not event.target's current ancestry.
  const path = event.composedPath();
  if (path.includes(dialog)) {
    return false;
  }
  // Clicks in top-layer UI above the modal (menus, toasts, tooltips, alerts) or
  // in another dialog aren't outside clicks
  if (path.some(isTopLayerElement)) {
    return false;
  }
  return true;
}

function isTopLayerElement(target: EventTarget): boolean {
  return (
    target instanceof Element && (target.tagName === 'DIALOG' || target.hasAttribute('popover'))
  );
}

/**
 * Floating panels are shown as manual popovers so they enter the top layer and paint
 * above other top-layer UI (anchored popovers, toasts). A dialog shown as a popover
 * never gets the `open` attribute, so shown-state is mirrored in `data-open` for the
 * stack selectors above and the Dialog styles.
 */
export function showFloatingPanel(dialog: HTMLDialogElement): void {
  try {
    dialog.showPopover();
  } catch {
    // Already in the top layer — the attribute below still applies.
  }
  dialog.setAttribute('data-open', '');
}

export function hideFloatingPanel(dialog: HTMLDialogElement): void {
  dialog.removeAttribute('data-open');
  try {
    dialog.hidePopover();
  } catch {
    // Never shown, or already hidden.
  }
}

/**
 * showModal() makes everything outside the dialog inert natively; `standard`-mode
 * surfaces (non-modal dialog.show()) apply the equivalent by hand, leaving
 * top-layer elements (dialogs and popovers such as toasts and menus) interactive.
 * Only returns elements this call inerted, so an inert attribute set elsewhere is
 * left alone on cleanup. Excludes Floating UI's root element, used by
 * click-to-component in dev.
 */
export function inertBackground(): HTMLElement[] {
  const inerted: HTMLElement[] = [];
  for (const child of document.body.children) {
    if (
      child instanceof HTMLElement &&
      !child.matches('dialog, [popover], [data-hero-portal]') &&
      !child.inert
    ) {
      child.inert = true;
      inerted.push(child);
    }
  }
  return inerted;
}

// Pseudo-class selectors (:modal, :popover-open) throw in some engines (jsdom);
// swallow that and treat it as "no match".
function safeQuerySelector(selector: string): Element | null {
  try {
    return document.querySelector(selector);
  } catch {
    return null;
  }
}

function safeQuerySelectorAll<T extends Element>(selector: string): T[] {
  try {
    return Array.from(document.querySelectorAll<T>(selector));
  } catch {
    return [];
  }
}
